import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import type { BulkCodeResponse } from '../../services/discounts.js';

interface CodeBulkResultDialogProps {
  result: BulkCodeResponse | null;
  /** 391: id → code text, so each row names its code; the id is shown when absent. */
  labels?: Record<string, string>;
  onClose: () => void;
}

/** 212 (#307): per-row outcome report for the shared-marker bulk actions — the
 * same shape as `users/BulkResultDialog.tsx`, but that component is typed to
 * `user_id`/`BulkUser` (037) and does not generalize to a code id cleanly, so
 * this is a small sibling rather than a forced shared abstraction. */
export function CodeBulkResultDialog({ result, labels, onClose }: CodeBulkResultDialogProps) {
  if (!result) return null;

  return (
    <Modal open onClose={onClose} title="Bulk action results">
      <div className="px-6 py-4 space-y-4 text-sm">
        <p className="text-steel">
          {result.summary.succeeded} succeeded, {result.summary.failed} failed (
          {result.summary.requested} requested)
        </p>
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {result.results.map((row) => (
            <li
              key={row.code_id}
              data-testid="code-bulk-result-row"
              className="flex items-start justify-between gap-4 rounded border border-hairline-soft px-3 py-2"
            >
              <span className="font-mono text-ink">{labels?.[row.code_id] ?? row.code_id}</span>
              {row.ok ? (
                <span className="shrink-0 font-medium text-green-600">OK</span>
              ) : (
                <span className="text-right text-red-600">{row.message ?? 'Failed'}</span>
              )}
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
